import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { IFileUpload, IFileUploadFailResponse } from '../file-upload/file-upload.service';
import { GaMessageService } from '../message/ga-message.service';
import { AuthService } from '../services/auth/auth.service';
import { TranslationService } from '../translation/translation.service';
import { IReportCellRendererValue, IReportCellRendererValueProp } from '../report/report-style.service';

interface IReportDocumentRendererParams extends ICellRendererParams {
  url:string;
  value:IReportCellRendererValue;
  aggregateRenderer(params:ICellRendererParams):string;
  getValueFormatted(params:IReportDocumentRendererParams):string;
}

@Component({
  selector: 'ag-report-document-renderer',
  template: `
    <ng-container *ngIf="params.value; else textRenderTemplate">
      <!-- only when this row is not in pinned state -->
      <ng-container *ngIf="!params.node.isRowPinned(); else rowPinnedTemplate">
        <ng-container *ngIf="params.value?.properties?.documentId; else fileUpload">
          <a [href]="url" target="_blank">{{params.getValueFormatted(params)}}</a>
        </ng-container>

        <ng-template #fileUpload>
          <file-button class="btn-primary" [autoUpload]="true" [url]="url" [formData]="uploadFormData" [submitting]="submitting" [uploading]="uploading" (onUploadStarted)="onUploadStarted()" (onUploadDone)="onUploadDone($event)" (onUploadFailed)="onUploadFailed($event)">
            <i class="fa fa-folder-open-o" *ngIf="!uploading"></i>&nbsp;<span>{{params.getValueFormatted(params)}}</span>
          </file-button>
        </ng-template>
      </ng-container>

      <!-- row pinned state -->
      <ng-template #rowPinnedTemplate>
        <span>{{params.aggregateRenderer(params)}}</span>
      </ng-template>
    </ng-container>

    <ng-template #textRenderTemplate>
      <!-- it means this is row being grouped (row group) -->
      <span>{{params.getValueFormatted(params)}}</span>
    </ng-template>
  `
})
export class AgReportDocumentRendererComponent implements ICellRendererAngularComp {
  public params!:IReportDocumentRendererParams;
  public url:string;
  public uploadFormData:IReportCellRendererValueProp;
  public submitting:boolean;
  public uploading:boolean;

  constructor(
    private authService:AuthService,
    private messageService:GaMessageService,
    private translationService:TranslationService
  ) {
    /**/
  }

  public agInit(params:IReportDocumentRendererParams):void {
    this.params = params;
    // set url value
    this.initCellRenderer();
  }

  public refresh(params:IReportDocumentRendererParams):boolean {
    this.params = params;
    // refresh url value
    this.initCellRenderer();
    // As we have updated the params we return true to let AG Grid know we have handled the refresh.
    // So AG Grid will not recreate the cell renderer from scratch.
    return true;
  }

  public onUploadStarted():void {
    this.uploading = true;
  }

  public onUploadDone(result:IFileUpload):void {
    this.uploadFormData = undefined;
    this.uploading = undefined;

    this.params.data[ this.params.colDef.field ] = result.response?.data;
    this.params.refreshCell();
  }

  public onUploadFailed(response:IFileUploadFailResponse):void {
    // error message prefix
    const MSG_ERROR_PREFIX:string = this.translationService.instant('GA.FORM.UPLOAD.ERROR_UPLOAD');
    // set uploading status to false
    this.uploading = false;
    if (response) {
      let errorMessage:string = this.translationService.instant('GA.FORM.UPLOAD.UPLOAD_FAIL_MSG', { message: response.message });
      if (response.status) {
        errorMessage += ` (${response.status}${response.error? ' ' + response.error : ''})`;
        this.messageService.showResponseErrorMessage(MSG_ERROR_PREFIX, errorMessage, response);
      }
    } else {
      this.messageService.showResponseErrorMessage(MSG_ERROR_PREFIX, 'FAIL', response);
    }
  }

  private initCellRenderer():void {
    const value:IReportCellRendererValue = this.params.value;

    if (value) {
      if (value.properties?.documentId) {
        this.url = this.authService.authenticatedUrl(`rest/reports/${this.params.context?.reportId}/documents/${value.properties?.documentId}`);
      } else {
        this.uploadFormData = value.properties;
        this.uploading = false;
        this.url = this.authService.authenticatedUrl(`rest/reports/${this.params.context?.reportId}/documents/`);
      }
    }
  }
}